import React, { useCallback, useEffect, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import dayjs from "dayjs";
import CustomTabs from "../../common/custom-tabs";
import typography from "../../cssUtils/typography";
import { grayFontColor } from "../../cssUtils/constants";
import { useAuth } from "../../hooks/AuthProvider";
import { EMI_REMINDERS_SCHEDULE_ENDPOINT } from "../../config/urls";
import EMIRecordsTab from "./EMIRecordsTab";
import EMIUploadTab from "./EMIUploadTab";
// Schedule settings temporarily hidden (BB-2758)
// import EMIScheduleSettingsTab from "./EMIScheduleSettingsTab";

const TABS = [
  { value: "records", label: "Records" },
  { value: "upload", label: "Upload" },
  // { value: "schedule", label: "Schedule settings" },
];

const MonthNavigator = ({ month, onChange }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1,
      py: 0.5,
      bgcolor: "#FFFFFF",
      border: "1px solid #E1E8F2",
      borderRadius: 2,
    }}
  >
    <IconButton size="small" onClick={() => onChange(month.subtract(1, "month"))} aria-label="Previous month">
      <ChevronLeft fontSize="small" />
    </IconButton>
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, minWidth: 120, textAlign: "center" }}>
      {month.format("MMMM YYYY")}
    </Typography>
    <IconButton size="small" onClick={() => onChange(month.add(1, "month"))} aria-label="Next month">
      <ChevronRight fontSize="small" />
    </IconButton>
  </Box>
);

const scheduleSubtitle = (stages) => {
  const labels = stages
    .filter((s) => s.enabled && s.days_before != null)
    .map((s) => s.days_before)
    .sort((a, b) => b - a)
    .map((d) => `D-${d}`);
  if (!labels.length) return "No WhatsApp reminders scheduled";
  return `${labels.length} WhatsApp reminder${labels.length === 1 ? "" : "s"} per cycle · ${labels.join(
    ", ",
  )} before each due date`;
};

const EMIReminderScreen = () => {
  const { getValidToken } = useAuth();
  const [activeTab, setActiveTab] = useState("records");
  const [month, setMonth] = useState(() => dayjs().startOf("month"));
  const [subtitle, setSubtitle] = useState("");

  const fetchSchedule = useCallback(async () => {
    try {
      const token = await getValidToken();
      const response = await fetch(EMI_REMINDERS_SCHEDULE_ENDPOINT, {
        headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": localStorage.getItem("tenant") },
      });
      if (!response.ok) throw new Error("Failed to fetch EMI schedule");
      const data = await response.json();
      setSubtitle(data.configured ? scheduleSubtitle(data.stages) : "");
    } catch (error) {
      console.error("Error fetching EMI schedule:", error);
    }
  }, [getValidToken]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}
      >
        <Box>
          <Typography sx={typography.h1}>EMI Reminders</Typography>
          {subtitle && (
            <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>{subtitle}</Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <CustomTabs value={activeTab} onChange={(_, val) => setActiveTab(val)} tabs={TABS} />
          {activeTab === "records" && <MonthNavigator month={month} onChange={setMonth} />}
        </Box>
      </Box>

      {activeTab === "records" && <EMIRecordsTab month={month.format("YYYY-MM")} />}
      {activeTab === "upload" && <EMIUploadTab />}
      {/* {activeTab === "schedule" && <EMIScheduleSettingsTab onSaved={fetchSchedule} />} */}
    </Box>
  );
};

export default EMIReminderScreen;
